import React, {useState, useEffect, useMemo, useRef} from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    ChartOptions,
    ChartData,
    TooltipItem,
    Plugin
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Chart } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import moment from 'moment';
import { getTimeSeriesData, getAnnotationPeriods } from '../../services/api.service';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, LineController, Title, Tooltip, Legend, TimeScale, zoomPlugin);

interface TimeSeriesDataPoint {
    timestamp: string;
    value: number | null;
}

interface TimeSeriesData {
    data: TimeSeriesDataPoint[];
    metadata: {
        station_id: string;
        variable: string;
        start_date: string;
        end_date: string;
        count: number;
        aggregation: string;
        total_count: number;
    };
}


interface BatteryPeriod {
    start: string;
    end: string;
    text: string;
}

interface BatteryVoltageChartProps {
    stationId: string;
    startDate: string;
    endDate: string;
    height?: number;
     showSevereBatteryIssues?: boolean;
     showImminentBatteryFailure?: boolean;
     showRapidBatteryDrop?: boolean;
     showLowBatteryCapacity?: boolean;
}

const BatteryVoltageChart: React.FC<BatteryVoltageChartProps> = ({
    stationId,
    startDate,
    endDate,
    height = 400,
    showSevereBatteryIssues = false,
     showImminentBatteryFailure = false,
    showRapidBatteryDrop = false,
    showLowBatteryCapacity = false,
}) => {
    // State management
    const [batteryLevelData, setBatteryLevelData] = useState<TimeSeriesData | null>(null);
    const [batteryVoltageData, setBatteryVoltageData] = useState<TimeSeriesData | null>(null);
     const [severeBatteryPeriods, setSevereBatteryPeriods] = useState<BatteryPeriod[]>([]);
    const [severeBatteryPeriodsCache, setSevereBatteryPeriodsCache] = useState<BatteryPeriod[]>([]);
    const [imminentFailurePeriods, setImminentFailurePeriods] = useState<BatteryPeriod[]>([]);
    const [imminentFailurePeriodsCache, setImminentFailurePeriodsCache] = useState<BatteryPeriod[]>([]);
    const [rapidBatteryDropPeriods, setRapidBatteryDropPeriods] = useState<BatteryPeriod[]>([]);
    const [rapidBatteryDropPeriodsCache, setRapidBatteryDropPeriodsCache] = useState<BatteryPeriod[]>([]);
    const [lowBatteryCapacityPeriods, setLowBatteryCapacityPeriods] = useState<BatteryPeriod[]>([]);
    const [lowBatteryCapacityPeriodsCache, setLowBatteryCapacityPeriodsCache] = useState<BatteryPeriod[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [chartKey, setChartKey] = useState<number>(0); // Add key to force chart re-render

    const chartRef = useRef<any>(null);

     // Reset zoom function that properly accesses the chart instance
    const resetZoom = () => {
        if (chartRef.current) {
            // Access the chart instance through the react-chartjs-2 wrapper
            const chartInstance = chartRef.current;
            if (chartInstance && chartInstance.resetZoom) {
                chartInstance.resetZoom();
            } else if (chartInstance && chartInstance.chart && chartInstance.chart.resetZoom) {
                chartInstance.chart.resetZoom();
            }
        }
    };

    const adjustedStartDate = useMemo(() =>
        moment(startDate).subtract(3, 'month').format('YYYY-MM-DDTHH:mm:ss'),
        [startDate]
    );

    // Data fetching effect
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                // Fetch both datasets in parallel
                const [batteryLevelResponse, batteryVoltageResponse] = await Promise.allSettled([
                    getTimeSeriesData(stationId, 'lb', adjustedStartDate, endDate), // Battery level
                    getTimeSeriesData(stationId, 'lv', adjustedStartDate, endDate)  // Battery voltage
                ]);

                // Handle battery level data response
                if (batteryLevelResponse.status === 'fulfilled') {
                    setBatteryLevelData(batteryLevelResponse.value);
                } else {
                    console.error('Failed to fetch battery level data:', batteryLevelResponse.reason);
                }

                // Handle battery voltage data response
                if (batteryVoltageResponse.status === 'fulfilled') {
                    setBatteryVoltageData(batteryVoltageResponse.value);
                } else {
                    console.error('Failed to fetch battery voltage data:', batteryVoltageResponse.reason);
                }

            } catch (err) {
                setError('Failed to fetch battery data');
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [stationId, adjustedStartDate, endDate]);

            // Separate effect for severe battery periods
    useEffect(() => {
        const fetchSevereBatteryPeriods = async () => {
            try {
                const severeBatteryResponse = await getAnnotationPeriods(stationId, 'severe_battery_issues', startDate, endDate);
                const periods = severeBatteryResponse.periods || [];

                // Always cache the fetched data
                setSevereBatteryPeriodsCache(periods);

                // Only display if toggle is enabled
                if (showSevereBatteryIssues) {
                    setSevereBatteryPeriods(periods);
                } else {
                    setSevereBatteryPeriods([]);
                }
            } catch (err) {
                console.error('Failed to fetch severe battery periods:', err);
                setSevereBatteryPeriodsCache([]);
                setSevereBatteryPeriods([]);
            }
        };

        fetchSevereBatteryPeriods();
    }, [stationId, startDate, endDate, showSevereBatteryIssues]);

        // Effect to handle toggle changes using cached data
    useEffect(() => {
        if (showSevereBatteryIssues) {
            setSevereBatteryPeriods(severeBatteryPeriodsCache);
        } else {
            setSevereBatteryPeriods([]);
        }
        setChartKey(prev => prev + 1);
    }, [showSevereBatteryIssues, severeBatteryPeriodsCache]);

        // Separate effect for imminent failure periods
    useEffect(() => {
        const fetchImminentFailurePeriods = async () => {
            try {
                const imminentFailureResponse = await getAnnotationPeriods(stationId, 'imminent_battery_failure', startDate, endDate);
                const periods = imminentFailureResponse.periods || [];

                // Always cache the fetched data
                setImminentFailurePeriodsCache(periods);

                // Only display if toggle is enabled
                if (showImminentBatteryFailure) {
                    setImminentFailurePeriods(periods);
                } else {
                    setImminentFailurePeriods([]);
                }
            } catch (err) {
                console.error('Failed to fetch imminent failure periods:', err);
                setImminentFailurePeriodsCache([]);
                setImminentFailurePeriods([]);
            }
        };

        fetchImminentFailurePeriods();
    }, [stationId, startDate, endDate, showImminentBatteryFailure]);

    useEffect(() => {
    if (showImminentBatteryFailure) {
        setImminentFailurePeriods(imminentFailurePeriodsCache);
    } else {
        setImminentFailurePeriods([]);
    }
    setChartKey(prev => prev + 1);
}, [showImminentBatteryFailure, imminentFailurePeriodsCache]);


     // Rapid battery drop periods effect
    useEffect(() => {
        const fetchRapidBatteryDropPeriods = async () => {
            try {
                const rapidDropResponse = await getAnnotationPeriods(stationId, 'rapid_battery_drop', startDate, endDate);
                const periods = rapidDropResponse.periods || [];

                setRapidBatteryDropPeriodsCache(periods);

                if (showRapidBatteryDrop) {
                    setRapidBatteryDropPeriods(periods);
                } else {
                    setRapidBatteryDropPeriods([]);
                }
            } catch (err) {
                console.error('Failed to fetch rapid battery drop periods:', err);
                setRapidBatteryDropPeriodsCache([]);
                setRapidBatteryDropPeriods([]);
            }
        };

        fetchRapidBatteryDropPeriods();
    }, [stationId, startDate, endDate, showRapidBatteryDrop]);

    useEffect(() => {
        if (showRapidBatteryDrop) {
            setRapidBatteryDropPeriods(rapidBatteryDropPeriodsCache);
        } else {
            setRapidBatteryDropPeriods([]);
        }
        setChartKey(prev => prev + 1);
    }, [showRapidBatteryDrop, rapidBatteryDropPeriodsCache]);

      // Low Battery Capacity effect
    useEffect(() => {
        const fetchLowBatteryCapacityPeriods = async () => {
            try {
                const lowCapacityResponse = await getAnnotationPeriods(stationId, 'low_capacity', startDate, endDate);
                const periods = lowCapacityResponse.periods || [];

                setLowBatteryCapacityPeriodsCache(periods);

                if (showLowBatteryCapacity) {
                    setLowBatteryCapacityPeriods(periods);
                } else {
                    setLowBatteryCapacityPeriods([]);
                }
            } catch (err) {
                console.error('Failed to fetch low battery capacity periods:', err);
                setLowBatteryCapacityPeriodsCache([]);
                setLowBatteryCapacityPeriods([]);
            }
        };

        fetchLowBatteryCapacityPeriods();
    }, [stationId, startDate, endDate, showLowBatteryCapacity]);

    useEffect(() => {
        if (showLowBatteryCapacity) {
            setLowBatteryCapacityPeriods(lowBatteryCapacityPeriodsCache);
        } else {
            setLowBatteryCapacityPeriods([]);
        }
        setChartKey(prev => prev + 1);
    }, [showLowBatteryCapacity, lowBatteryCapacityPeriodsCache]);


       // Create annotation plugin for battery issues
    const batteryAnnotationPlugin: Plugin<'line'> = useMemo(() => ({
        id: 'batteryAnnotation',
        beforeDraw: (chart: any) => {
            const ctx = chart.ctx;
            const chartArea = chart.chartArea;
            const xScale = chart.scales.x;

             // Helper function to draw text labels on periods
            const drawPeriodLabel = (startX: number, endX: number, text: string, color: string, yOffset: number = 0) => {
                const centerX = (startX + endX) / 2;
                const labelY = chartArea.top + 25 + yOffset;

                if (centerX >= chartArea.left && centerX <= chartArea.right) {
                    ctx.save();

                    // Measure text for background sizing
                    ctx.font = 'bold 12px Arial';
                    const textMetrics = ctx.measureText(text);
                    const textWidth = textMetrics.width;
                    const padding = 8;

                    // Draw background rectangle
                    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
                    ctx.fillRect(
                        centerX - textWidth/2 - padding,
                        labelY - 15,
                        textWidth + padding * 2,
                        20
                    );

                    // Draw border
                    ctx.strokeStyle = color;
                    ctx.lineWidth = 2;
                    ctx.strokeRect(
                        centerX - textWidth/2 - padding,
                        labelY - 15,
                        textWidth + padding * 2,
                        20
                    );

                    // Draw text
                    ctx.fillStyle = color;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(text, centerX, labelY - 5);

                    ctx.restore();
                }
            };

            // Helper function to draw dotted vertical lines
            const drawDottedLine = (x: number, color: string) => {
                if (x >= chartArea.left && x <= chartArea.right) {
                    ctx.save();
                    ctx.strokeStyle = color;
                    ctx.lineWidth = 2;
                    ctx.setLineDash([5, 5]);
                    ctx.beginPath();
                    ctx.moveTo(x, chartArea.top);
                    ctx.lineTo(x, chartArea.bottom);
                    ctx.stroke();
                    ctx.restore();
                }
            };


              // Draw severe battery issues (red)
            if (showSevereBatteryIssues && severeBatteryPeriods.length > 0) {
                ctx.save();
                ctx.fillStyle = 'rgba(220, 53, 69, 0.1)';

                severeBatteryPeriods.forEach(period => {
                    const startX = xScale.getPixelForValue(moment(period.start).valueOf());
                    const endX = xScale.getPixelForValue(moment(period.end).valueOf());

                    if (startX <= chartArea.right && endX >= chartArea.left) {
                        ctx.fillRect(
                            Math.max(startX, chartArea.left),
                            chartArea.top,
                            Math.min(endX, chartArea.right) - Math.max(startX, chartArea.left),
                            chartArea.bottom - chartArea.top
                        );

                        // Draw period label
                        drawPeriodLabel(
                            Math.max(startX, chartArea.left),
                            Math.min(endX, chartArea.right),
                            'Severe Battery Issues',
                            '#dc3545',
                            0
                        );

                    }
                });
                ctx.restore();

                 // Draw dotted lines for period boundaries
                severeBatteryPeriods.forEach(period => {
                    const startX = xScale.getPixelForValue(moment(period.start).valueOf());
                    const endX = xScale.getPixelForValue(moment(period.end).valueOf());

                    drawDottedLine(startX, 'rgba(220, 53, 69, 0.8)');
                    drawDottedLine(endX, 'rgba(220, 53, 69, 0.8)');
                });

            }

            // Draw imminent failure periods (green)
            if (showImminentBatteryFailure && imminentFailurePeriods.length > 0) {
                ctx.save();
                ctx.fillStyle = 'rgba(40, 167, 69, 0.2)';

                imminentFailurePeriods.forEach(period => {
                    const startX = xScale.getPixelForValue(moment(period.start).valueOf());
                    const endX = xScale.getPixelForValue(moment(period.end).valueOf());

                    if (startX <= chartArea.right && endX >= chartArea.left) {
                        ctx.fillRect(
                            Math.max(startX, chartArea.left),
                            chartArea.top,
                            Math.min(endX, chartArea.right) - Math.max(startX, chartArea.left),
                            chartArea.bottom - chartArea.top
                        );
                    }

                     // Draw period label with offset to avoid overlap
                    const yOffset = showSevereBatteryIssues ? 30 : 0;
                    drawPeriodLabel(
                        Math.max(startX, chartArea.left),
                        Math.min(endX, chartArea.right),
                        'Imminent Failure',
                        '#ff9500',
                        yOffset
                    );

                });
                ctx.restore();

                  // Draw dotted lines
                imminentFailurePeriods.forEach(period => {
                    const startX = xScale.getPixelForValue(moment(period.start).valueOf());
                    const endX = xScale.getPixelForValue(moment(period.end).valueOf());

                    drawDottedLine(startX, 'rgba(40, 167, 69, 0.8)');
                    drawDottedLine(endX, 'rgba(40, 167, 69, 0.8)');
                });

            }


             // Draw rapid battery drop periods (blue)
            if (showRapidBatteryDrop && rapidBatteryDropPeriods.length > 0) {
                ctx.save();
                ctx.fillStyle = 'rgba(0, 123, 255, 0.2)';

                rapidBatteryDropPeriods.forEach(period => {
                    const startX = xScale.getPixelForValue(moment(period.start).valueOf());
                    const endX = xScale.getPixelForValue(moment(period.end).valueOf());

                    if (startX <= chartArea.right && endX >= chartArea.left) {
                        ctx.fillRect(
                            Math.max(startX, chartArea.left),
                            chartArea.top,
                            Math.min(endX, chartArea.right) - Math.max(startX, chartArea.left),
                            chartArea.bottom - chartArea.top
                        );

                        // Draw period label with offset to avoid overlap
                    const yOffset = (showSevereBatteryIssues ? 30 : 0) + (showImminentBatteryFailure ? 30 : 0);
                    drawPeriodLabel(
                        Math.max(startX, chartArea.left),
                        Math.min(endX, chartArea.right),
                        'Rapid Battery Drop',
                        '#007bff',
                        yOffset
                    );

                    }
                });
                ctx.restore();

                // Draw dotted lines
                rapidBatteryDropPeriods.forEach(period => {
                    const startX = xScale.getPixelForValue(moment(period.start).valueOf());
                    const endX = xScale.getPixelForValue(moment(period.end).valueOf());

                    drawDottedLine(startX, 'rgba(0, 123, 255, 0.8)');
                    drawDottedLine(endX, 'rgba(0, 123, 255, 0.8)');
                });

            }

             // Draw low battery capacity periods (blue)
            if (showLowBatteryCapacity && lowBatteryCapacityPeriods.length > 0) {
                ctx.save();
                ctx.fillStyle = 'rgba(255, 193, 7, 0.15)';

                lowBatteryCapacityPeriods.forEach(period => {
                    const startX = xScale.getPixelForValue(moment(period.start).valueOf());
                    const endX = xScale.getPixelForValue(moment(period.end).valueOf());

                    if (startX <= chartArea.right && endX >= chartArea.left) {
                        ctx.fillRect(
                            Math.max(startX, chartArea.left),
                            chartArea.top,
                            Math.min(endX, chartArea.right) - Math.max(startX, chartArea.left),
                            chartArea.bottom - chartArea.top
                        );

                        // Draw period label with offset to avoid overlap
                    const yOffset = (showSevereBatteryIssues ? 30 : 0) + (showImminentBatteryFailure ? 30 : 0) + (showRapidBatteryDrop ? 30 : 0);
                    drawPeriodLabel(
                        Math.max(startX, chartArea.left),
                        Math.min(endX, chartArea.right),
                        'Low Battery Capacity',
                        '#e0a800',
                        yOffset
                    );

                    }
                });
                ctx.restore();

                // Draw dotted lines
                lowBatteryCapacityPeriods.forEach(period => {
                    const startX = xScale.getPixelForValue(moment(period.start).valueOf());
                    const endX = xScale.getPixelForValue(moment(period.end).valueOf());

                    drawDottedLine(startX, 'rgba(255, 193, 7, 0.8)');
                    drawDottedLine(endX, 'rgba(255, 193, 7, 0.8)');
                });

            }


        }
    }), [showSevereBatteryIssues, severeBatteryPeriods, showImminentBatteryFailure, imminentFailurePeriods,
        showRapidBatteryDrop, rapidBatteryDropPeriods, showLowBatteryCapacity, lowBatteryCapacityPeriods]);


    // Chart data configuration
    const chartData: ChartData<'line'> = useMemo(() => {
        const datasets: any[] = [];

         // Battery Level dataset - raw data
        if (batteryLevelData?.data && batteryLevelData.data.length > 0) {
            datasets.push({
                label: 'Battery Percentage (%)',
                data: batteryLevelData.data.map(d => ({
                    x: moment(d.timestamp).valueOf(),
                    y: d.value  // Keep null values as null for gaps
                })),
                borderColor: 'rgb(255, 99, 132)',
                backgroundColor: 'rgba(255, 99, 132, 0.1)',
                yAxisID: 'y',
                tension: 0.1,
                pointRadius: 1,
                pointHoverRadius: 4,
                spanGaps: false  // Show gaps where data is missing
            });
        }

        // Battery Voltage dataset - raw data
        if (batteryVoltageData?.data && batteryVoltageData.data.length > 0) {
            datasets.push({
                label: 'Battery Voltage (mAh)',
                data: batteryVoltageData.data.map(d => ({
                    x: moment(d.timestamp).valueOf(),
                    y: d.value  // Keep null values as null for gaps
                })),
                borderColor: 'rgb(54, 162, 235)',
                backgroundColor: 'rgba(54, 162, 235, 0.1)',
                yAxisID: 'y1',
                tension: 0.1,
                pointRadius: 1,
                pointHoverRadius: 4,
                spanGaps: false  // Show gaps where data is missing
            });
        }

         // Add legend entries for annotations
        if (showSevereBatteryIssues && severeBatteryPeriods.length > 0) {
            datasets.push({
                label: 'Severe Battery Issues',
                data: [],
                backgroundColor: 'rgba(220, 53, 69, 0.1)',
                borderColor: 'rgba(220, 53, 69, 0.8)',
                pointRadius: 0
            });
        }

        if (showImminentBatteryFailure && imminentFailurePeriods.length > 0) {
            datasets.push({
                label: 'Imminent Battery Failure',
                data: [],
                backgroundColor: 'rgba(40, 167, 69, 0.1)',
                borderColor: 'rgba(40, 167, 69, 0.8)',
                pointRadius: 0
            });
        }

        if (showRapidBatteryDrop && rapidBatteryDropPeriods.length > 0) {
            datasets.push({
                label: 'Rapid Battery Drop',
                data: [],
                backgroundColor: 'rgba(0, 123, 255, 0.1)',
                borderColor: 'rgba(0, 123, 255, 0.8)',
                pointRadius: 0
            });
        }

        return { datasets };

    }, [batteryLevelData, batteryVoltageData, showSevereBatteryIssues, severeBatteryPeriods, showImminentBatteryFailure, imminentFailurePeriods, showRapidBatteryDrop, rapidBatteryDropPeriods]);

    // Chart options configuration
    const chartOptions: ChartOptions<'line'> = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
         animation: false,
        interaction: {
             mode: 'index',
            intersect: false,
        },
        plugins: {
            title: {
                display: true,
                text: 'Battery and Battery Voltage',
                font: {
                    size: 16
                }
            },
            legend: {
                position: 'top'

            },
            tooltip: {
                callbacks: {
                    title: (context: TooltipItem<'line'>[]) => {
                        if (context.length > 0) {
                            return moment(context[0].parsed.x).format('YYYY-MM-DD HH:mm');
                        }
                        return '';
                    },
                }
            },
             // Zoom plugin configuration
            zoom: {
                zoom: {
                    wheel: {
                        enabled: true,
                    },
                    pinch: {
                        enabled: true
                    },
                    mode: 'x',
                },
                pan: {
                    enabled: true,
                    mode: 'x',
                }
            }
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    displayFormats: {
                        hour: 'DD/MM',
                        day: 'DD/MM',
                        week: 'DD/MM',
                        month: 'MM'
                    },
                    tooltipFormat: 'DD MMM YYYY HH:mm'
                },
                title: {
                    display: true,
                    text: 'Date'
                }
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Battery Percentage (%)'
                },
                min: 0,
                max: 100
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                title: {
                    display: true,
                    text: 'Battery Voltage (V)'
                },
                grid: {
                    drawOnChartArea: false,
                },
            }

        },
    }), [stationId, showSevereBatteryIssues, showImminentBatteryFailure, showRapidBatteryDrop]);

    // Loading state
    if (loading) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: `${height}px` }}>
                        <div className="text-muted">
                            <i className="fa fa-spinner fa-spin fa-3x mb-3"></i>
                            <p>Loading battery and voltage data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="alert alert-danger">{error}</div>
                </div>
            </div>
        );
    }
    // Main render
    return (
        <div className="card">
               <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                    {/*<h5 className="card-title mb-0">{title}</h5>*/}
                    <div className="d-flex align-items-center gap-3">
                         {/* Reset zoom button */}
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={resetZoom}
                            title="Reset Zoom"
                        >
                            <i className="fa fa-search-minus me-1"></i>
                            Reset Zoom
                        </button>

                    </div>
                </div>
            </div>
            <div className="card-body">
                <div style={{ height: `${height}px` }}>
                    <Chart
                         key={chartKey}
                        ref={chartRef}
                        type="line"
                        data={chartData}
                        options={chartOptions}
                         plugins={[batteryAnnotationPlugin]}
                    />
                </div>
                {/* Aggregation metadata display */}
                    <div className="mt-2">
                        <small className="text-muted">
                            {batteryLevelData?.metadata?.aggregation || batteryVoltageData?.metadata?.aggregation
                                ? `Data aggregation: ${batteryLevelData?.metadata?.aggregation || batteryVoltageData?.metadata?.aggregation}`
                                : 'Raw measurements (no aggregation)'
                            }
                        </small>
                    </div>

            </div>
        </div>
    );
};

export default BatteryVoltageChart;